import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-spreadsheet-jackets';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Заказ курток из Китая через YOYbuy Sheet: пуховики, бомберы, косухи и всё об утеплителях',
  description: 'Полный гид по заказу верхней одежды из Китая с помощью yoybuy spreadsheet. Типы утеплителей, размерные сетки, проверка качества строчек и фурнитуры.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  image: 'https://readdy.ai/api/search-image?query=Minimalist%20fashion%20photography%20of%20a%20stylish%20olive%20green%20bomber%20jacket%20hanging%20on%20a%20minimal%20wooden%20hanger%20against%20a%20soft%20cream%20wall%2C%20natural%20daylight%20streaming%20from%20a%20nearby%20window%2C%20visible%20texture%20of%20nylon%20fabric%20and%20ribbed%20cuffs%2C%20editorial%20outerwear%20photography%2C%20warm%20neutral%20aesthetic%2C%20high%20detail%20on%20zippers%20and%20stitching&width=1400&height=700&seq=article-jackets-hero-2026&orientation=landscape',
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

export default function JacketsArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Куртки</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Заказ курток из Китая через YOYbuy Sheet: пуховики, бомберы, косухи и всё об утеплителях
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Полный гид по заказу верхней одежды из Китая с помощью <strong>yoybuy spreadsheet</strong>. Типы утеплителей, размерные сетки, проверка качества строчек и фурнитуры.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 8 минут</span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20fashion%20photography%20of%20a%20stylish%20olive%20green%20bomber%20jacket%20hanging%20on%20a%20minimal%20wooden%20hanger%20against%20a%20soft%20cream%20wall%2C%20natural%20daylight%20streaming%20from%20a%20nearby%20window%2C%20visible%20texture%20of%20nylon%20fabric%20and%20ribbed%20cuffs%2C%20editorial%20outerwear%20photography%2C%20warm%20neutral%20aesthetic%2C%20high%20detail%20on%20zippers%20and%20stitching&width=1400&height=700&seq=article-jackets-hero-2026&orientation=landscape"
                  alt="Заказ курток из Китая через YOYbuy Sheet"
                  title="YOYbuy Sheet куртки — заказ верхней одежды из Китая"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Какие куртки выгоднее всего заказывать из Китая через YOYbuy Sheet
                  </h2>
                  <p>
                    Верхняя одежда из Китая — это оптимальное соотношение цены и качества. Китайские фабрики производят пуховики, парки, бомберы и кожаные куртки для мировых брендов, а через <strong>yoybuy spreadsheet</strong> вы получаете их напрямую, без посредников и розничных наценок. Экономия может достигать 60–70% от цены в европейском магазине.
                  </p>
                  <p>
                    <strong>Yoybuy sheet</strong> для категории «Куртки» включает поля для указания типа утеплителя, плотности наполнителя, материала верха, длины изделия и сезонности. Агент получает полную спецификацию и ищет товар строго по вашим критериям. Это особенно важно для зимней одежды, где от правильного выбора утеплителя зависит, будет ли вам тепло.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Утеплители: что выбрать и как указать в YOYbuy Spreadsheet
                  </h2>
                  <div className="space-y-4">
                    {[
                      { name: 'Натуральный пух (Down)', desc: 'Самый тёплый вариант. В yoybuy sheet укажите параметр Fill Power (FP): 600–700 FP для городской зимы, 800+ FP для сильных морозов. Соотношение пух/перо (например, 90/10) — тоже важный параметр в таблице.' },
                      { name: 'Синтепон (Polyester fill)', desc: 'Бюджетный синтетический утеплитель. Лёгкий, быстро сохнет. В yoybuy spreadsheet укажите плотность: 200–300 г/м² для осенней куртки, 400+ г/м² для зимней.' },
                      { name: 'Тинсулейт (Thinsulate)', desc: 'Высокотехнологичный тонкий утеплитель. Тёплый и лёгкий. В таблице YOYbuy отметьте вес утеплителя в граммах — агент найдёт модели с нужной плотностью.' },
                      { name: 'Холлофайбер (Hollow fiber)', desc: 'Современный синтетический наполнитель с полыми волокнами. Хорошо держит тепло, не сваливается. Укажите в yoybuy sheet плотность: от 200 г/м².' },
                    ].map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-accent-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="my-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Aesthetic%20flat%20lay%20photography%20of%20three%20different%20jacket%20styles%20neatly%20folded%20on%20a%20light%20oak%20surface%2C%20including%20a%20beige%20puffer%20jacket%2C%20a%20black%20leather%20jacket%2C%20and%20a%20khaki%20bomber%2C%20soft%20natural%20window%20lighting%2C%20neutral%20cream%20background%2C%20editorial%20fashion%20catalog%20style%2C%20clean%20minimal%20composition%2C%20high%20detail%20on%20fabrics%20and%20hardware&width=1200&height=600&seq=article-jackets-types&orientation=landscape"
                    alt="YOYbuy Sheet — типы курток: пуховик, бомбер, косуха"
                    title="Типы курток в YOYbuy Spreadsheet для заказа из Китая"
                    className="w-full h-auto object-cover"
                  />
                </div>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Проверка качества куртки: на что обратить внимание
                  </h2>
                  <p>
                    При заказе через <strong>yoybuy spreadsheet</strong> вы можете добавить в таблицу чек-лист проверки качества. Агент осмотрит каждую куртку перед отправкой по вашим критериям: ровность строчек, качество фурнитуры (молнии, кнопки, пуговицы), отсутствие торчащих ниток, равномерность распределения утеплителя. Особенно это важно для пуховиков — неравномерно распределённый пух делает куртку бесполезной в мороз.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Сезонность и выбор модели
                  </h2>
                  <p>
                    Через <strong>yoybuy spreadsheet</strong> удобно планировать заказы на разные сезоны. Осенние бомберы и ветровки заказывайте в июле-августе — к началу сезона они уже будут у вас. Зимние пуховики и парки — в сентябре-октябре. А лёгкие джинсовые куртки и косухи — в феврале-марте, чтобы успеть к весне. Таблица YOYbuy помогает отслеживать статус каждого заказа и планировать бюджет на сезон вперёд.
                  </p>
                  <p>
                    Готовы начать? Вернитесь на <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">главную страницу yoybuy.cyou</Link> и выберите категорию «Куртки». Или прочитайте другие статьи: <Link to="/yoybuy-sheet-guide" className="text-primary-600 hover:text-primary-700 underline font-medium">полное руководство по YOYbuy Sheet</Link> и <Link to="/yoybuy-spreadsheet-shipping" className="text-primary-600 hover:text-primary-700 underline font-medium">гид по доставке из Китая</Link>.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Читайте также:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/yoybuy-sheet-guide" className="text-xs text-primary-600 hover:text-primary-700 underline">Руководство по YOYbuy Sheet</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-hoodies" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ худи</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shoes" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ обуви</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">Главная</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-xs text-primary-600 hover:text-primary-700 underline">Куртки на kaspreadsheet</a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}